import { useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { createDailyCallProvider } from './dailyCallProvider'
import type { CallProviderFactory, CallProviderInput, CallSnapshot } from './call.types'
import { IDLE_CALL_SNAPSHOT } from './call.types'
import { CallContext } from './callContext'
import type { CallControls } from './callContext'

export interface CallProviderProps {
  input: CallProviderInput
  createProvider?: CallProviderFactory
  autoPreview?: boolean
  children: ReactNode
}

export function CallProvider({ input, createProvider = createDailyCallProvider, autoPreview = false, children }: CallProviderProps) {
  const [snapshot, setSnapshot] = useState<CallSnapshot>(IDLE_CALL_SNAPSHOT)
  const provider = useMemo(
    () => createProvider({ roomUrl: input.roomUrl, token: input.token, displayName: input.displayName }),
    [createProvider, input.roomUrl, input.token, input.displayName],
  )

  useEffect(() => {
    const unsubscribe = provider.subscribe(setSnapshot)
    if (autoPreview) void provider.preview()
    return () => {
      unsubscribe()
      void provider.leave()
    }
  }, [provider, autoPreview])

  const controls = useMemo<CallControls>(() => ({
    preview: () => { void provider.preview() },
    join: () => { void provider.join() },
    setMic: (on) => { void provider.setMic(on) },
    setCam: (on) => { void provider.setCam(on) },
    setScreen: (on) => { void provider.setScreen(on) },
    leave: () => provider.leave(),
    listDevices: () => provider.listDevices?.() ?? Promise.resolve([]),
    setCamera: (deviceId) => { void provider.setCamera?.(deviceId) },
    setMicrophone: (deviceId) => { void provider.setMicrophone?.(deviceId) },
    setSpeaker: (deviceId) => { void provider.setSpeaker?.(deviceId) },
  }), [provider])

  return <CallContext.Provider value={{ snapshot, controls }}>{children}</CallContext.Provider>
}
